/**
 * The track's story: where it is in the work (the steps) and the one thing to
 * do now. Pure, so it can be read in a test.
 *
 * Owner ruling 2026-10-20: a page opens with where this thing stands and the
 * one next act, and the rest steps back. The track page draws the steps as its
 * navigation and the next thing as its only call to action (2026-11-03: the
 * page used to say where the track is three times, and the three disagreed).
 *
 * The steps are the rows of the track summary card (trackSummaryRows), in the
 * order the work happens, read from the server's own readiness check and the
 * result published for the track. The next thing is chosen deterministically
 * from those facts and from where the ball is:
 *
 * - Before the first toss, the toss. Nothing on the track has to be set before
 *   the ball goes down: the Request carries intent and scope (glossary ›
 *   Request), the toss checks no lock (`catchball_send_the_ball`, migration
 *   744), and the people it goes to set the track up and run its sessions.
 *   Only running a session needs the setup (`run_session` refuses until then).
 * - Otherwise the first step that is not done; then publishing for the track;
 *   then, while the ball is still in play below the top, passing it on.
 *
 * The code decides what to say; nothing here is a model's guess.
 */
import type { ReactNode } from 'react';
import type { PopoverRowState } from '@/components/ds';
import {
  trackSummaryRows,
  type TrackSummaryBasics,
  type TrackSessionCounts,
  type TrackSummaryServerFacts,
} from '@/components/prioritization/tracks/trackSummaryRows';
import { formatDate } from '@/i18n/formatters';
import { CATCHBALL_LABELS } from '@/lib/catchball/vocabulary';

export const STORY_STOP_IDS = ['participants', 'pool', 'criteria', 'scales', 'values', 'sessions', 'published'] as const;
export type StoryStopId = (typeof STORY_STOP_IDS)[number];

export interface StoryStop {
  id: StoryStopId;
  label: string;
  /** Null while the fact is still being read, or when it could not be read. */
  state: PopoverRowState | null;
  value: ReactNode;
  note: ReactNode | null;
}

/** Opens one of the track's own steps, or leaves the page. */
export type StoryGo =
  | { kind: 'step'; step: StoryStopId }
  | { kind: 'href'; href: string }
  /** The first toss, in the toss drawer over this page (owner request 2026-11-03). */
  | { kind: 'toss'; cycleId: string; trackId: string };

/** Where the ball is for this track, as the page reads it. */
export interface StoryBall {
  /** Where tossing leads: the composer before the first toss, Catchball once it is in play. Null: the track is in no cycle, so it takes no part in Catchball. */
  send: { href: string; mode: 'compose' | 'inbox'; cycleId?: string; trackId?: string } | null;
  /** The live loop's canonical state (OPEN, PROPOSED, …); null when there is none. */
  loopState: string | null;
  /** True when the loop is not tossed yet and someone else holds the ball. */
  heldByOther: boolean;
}

const NO_BALL: StoryBall = { send: null, loopState: null, heldByOther: false };

export interface StoryNextThing {
  /** The stop this points at; null when nothing waits or nothing could be read. */
  stop: StoryStopId | null;
  title: string;
  why: string;
  /** The server's own word on what is in the way, when it has one. */
  detail: string | null;
  /**
   * The control that does it, named by what it does. Null when the signed-in
   * person may not act. `step` is the track step the act opens or belongs to;
   * the page leaves the button out while that step is already open below it.
   */
  act: { label: string; go: StoryGo; step: StoryStopId | null } | null;
  /** Who it waits on, when the signed-in person may not act. */
  waitsOn: string | null;
  /** True when the track's answer is published and nothing newer waits. */
  settled: boolean;
}

export interface TrackStory {
  stops: StoryStop[];
  /** Null while the facts are still being read. */
  next: StoryNextThing | null;
}

type StepStop = Exclude<StoryStopId, 'published'>;

/** Plain words for each stop that is not done yet: what to do when nothing is there, and when it is there but not locked. */
const STEP_WORDS: Record<StepStop, { open: string; partial: string; why: string; openLabel: string }> = {
  participants: {
    open: 'Name who takes part',
    partial: 'Lock who takes part',
    why: 'A session can rank only once the people are named and the list is locked.',
    openLabel: 'Open participation',
  },
  pool: {
    open: 'Put items in the pool',
    partial: 'Lock the item pool',
    why: 'The pool is the list the session ranks. It locks so everyone ranks the same list.',
    openLabel: 'Open the item pool',
  },
  criteria: {
    open: 'Choose the criteria',
    partial: 'Lock the criteria',
    why: 'Criteria are what the items are judged on.',
    openLabel: 'Open criteria',
  },
  scales: {
    open: 'Set the value scales',
    partial: 'Accept the value scales',
    why: 'A scale says what a high or a low value means on each criterion.',
    openLabel: 'Open values',
  },
  values: {
    open: 'Enter the values',
    partial: 'Agree the values',
    why: 'Values are each person’s estimate for each item on each criterion. They converge before the ranking.',
    openLabel: 'Open values',
  },
  sessions: {
    open: 'Run a ranking session',
    partial: 'Finish the ranking session',
    why: 'A session is where the group ranks the pool together.',
    openLabel: 'Open sessions',
  },
};

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const asText = (node: ReactNode): string | null => (typeof node === 'string' && node.trim() ? node : null);
/** A row's own blocker, in its words; nothing when the row only shows a count or a placeholder. */
const NOT_A_BLOCKER = new Set(['Checking…', 'Not known', 'Not ready', 'None yet', 'No pool yet', 'None chosen yet']);

function blockerOf(stop: StoryStop): string | null {
  const note = asText(stop.note);
  if (note && !NOT_A_BLOCKER.has(note)) return note.replace(/\.$/, '');
  if (stop.id === 'participants' || stop.id === 'scales' || stop.id === 'values') {
    const value = asText(stop.value);
    if (value && !NOT_A_BLOCKER.has(value)) return value.replace(/\.$/, '');
  }
  return null;
}

const unread = (title: string, why: string): StoryNextThing => ({ stop: null, title, why, detail: null, act: null, waitsOn: null, settled: false });

export function trackStory(
  basics: TrackSummaryBasics,
  sessions: TrackSessionCounts,
  server: TrackSummaryServerFacts,
  ball: StoryBall = NO_BALL,
): TrackStory {
  const rows = trackSummaryRows(basics, sessions, server);
  const byId = new Map(rows.filter((r) => r.id).map((r) => [r.id as string, r]));
  const stops: StoryStop[] = STORY_STOP_IDS.map((id) => {
    const r = byId.get(id);
    return { id, label: r ? asText(r.label) ?? id : id, state: r?.state ?? null, value: r?.value ?? '', note: r?.note ?? null };
  });

  if (server.readiness === undefined || server.published === undefined) return { stops, next: null };
  if (server.readiness === null) {
    return { stops, next: unread('Where this track stands could not be read.', 'The readiness check did not answer. Reload the page to try again.') };
  }

  const published = server.published;

  // Before the first toss: the toss is the one thing, and nothing on the track
  // has to be set for it. Once a session has started or a result is published
  // here, someone is already working the track, so its steps lead instead.
  if (ball.send?.mode === 'compose' && sessions.total === 0 && !published?.trackResultId) {
    return {
      stops,
      next: {
        stop: null,
        title: 'Toss the ball down',
        why: 'Nothing on this track has to be set before the toss. The people you send it to set up the track and run its sessions; anything you set here first, they start from.',
        detail: null,
        act: ball.heldByOther
          ? null
          : {
              label: CATCHBALL_LABELS.sendTheBall,
              go: ball.send.cycleId && ball.send.trackId
                ? { kind: 'toss', cycleId: ball.send.cycleId, trackId: ball.send.trackId }
                : { kind: 'href', href: ball.send.href },
              step: null,
            },
        waitsOn: ball.heldByOther ? 'Someone else holds the ball. Only the holder can toss it.' : null,
        settled: false,
      },
    };
  }

  const first = stops.find((s) => s.id !== 'published' && s.state !== 'done');
  if (first) {
    const words = STEP_WORDS[first.id as StepStop];
    return {
      stops,
      next: {
        stop: first.id,
        title: first.state === 'partial' ? words.partial : words.open,
        why: words.why,
        detail: blockerOf(first),
        act: { label: words.openLabel, go: { kind: 'step', step: first.id }, step: first.id },
        waitsOn: null,
        settled: false,
      },
    };
  }

  const resultHref = `${basics.href}/result`;
  if (published === null) {
    return { stops, next: unread('The published result could not be read.', 'The track’s result did not answer. Reload the page to try again.') };
  }
  const date = published.publishedAt ? formatDate(new Date(published.publishedAt), { day: 'numeric', month: 'short' }) : null;
  const answer = published.strategy === 'aggregate' ? `a result combined from ${plural(published.sourceCount, 'session')}` : published.sessionName ?? 'the published session';
  const act = published.mayPublish ? { label: 'Choose what to publish', go: { kind: 'href', href: resultHref } as StoryGo, step: 'published' as const } : null;
  const waitsOn = published.mayPublish ? null : 'The person who holds the ball decides what is published for this track.';

  if (!published.trackResultId) {
    const ranked = sessions.ranked === 1 ? 'One session has' : `${sessions.ranked} sessions have`;
    return {
      stops,
      next: {
        stop: 'published',
        title: 'Publish a result for the track',
        why: `${ranked} ranked the pool, and nothing has been published for the track yet. Nothing goes up through Catchball, and nothing reaches the Plan of Record, until it is.`,
        // The server's reason here says the same thing in other words; one telling is enough.
        detail: null,
        act,
        waitsOn,
        settled: false,
      },
    };
  }

  if (published.waitingCount > 0) {
    return {
      stops,
      next: {
        stop: 'published',
        title: `Publish ${published.latestClosedSessionName ?? 'the newer session'} as this track’s answer, or keep ${published.sessionName ?? 'the published result'}`,
        why: `${sentence(plural(published.waitingCount, 'session'))} closed after ${answer} was published${date ? ` on ${date}` : ''}. Until someone decides, the cycle still counts that answer.`,
        detail: null,
        act,
        waitsOn,
        settled: false,
      },
    };
  }

  // Published, and the ball is still in play below the top: it goes up from Catchball.
  if (ball.send?.mode === 'inbox' && ball.loopState === 'OPEN') {
    return {
      stops,
      next: {
        stop: null,
        title: 'Send the track’s answer up',
        why: `Its answer is ${answer}${date ? `, published ${date}` : ''}. It reaches the level above only when the ball goes back up.`,
        detail: null,
        act: { label: 'Open Catchball', go: { kind: 'href', href: ball.send.href }, step: null },
        waitsOn: null,
        settled: false,
      },
    };
  }

  return {
    stops,
    next: {
      stop: null,
      title: 'Nothing waits on this track.',
      why: `Its answer is ${answer}${date ? `, published ${date}` : ''}${published.publishedByName ? ` by ${published.publishedByName}` : ''}.`,
      detail: null,
      act: { label: 'See the result', go: { kind: 'href', href: resultHref }, step: 'published' },
      waitsOn: null,
      settled: true,
    },
  };
}
